import { limparJanelaDeVideo } from './agoraSession.js';

// MAPA DE JOGADORES DA PARTIDA
//
// Um só sítio a decidir quem é quem. Antes havia DUAS regras diferentes a
// competir no mesmo ecrã:
//
//   • o vídeo era colocado pelo uid do Agora (1 = A à esquerda, 2 = B à direita);
//   • o nome, os golos e a barra de pontos eram escolhidos por perspetiva
//     (`souOJogadorA ? nomeA : nomeB`), ou seja, trocavam consoante quem olhava.
//
// Para um espetador as duas regras davam resultados opostos: via a câmara do
// jogador A com o nome do jogador B por baixo, e o placar ao contrário. Agora
// tudo — nome, foto, golos, pontos, claque e janela de vídeo — sai deste mapa.
//
// Os lugares são FIXOS e iguais para toda a gente, nunca por ordem de chegada:
//   slot 1 = anfitrião  (idA) = janela esquerda = uid 1 no Agora
//   slot 2 = adversário (idB) = janela direita  = uid 2 no Agora

export const SLOT_ANFITRIAO = 1;
export const SLOT_ADVERSARIO = 2;

export const CONTENTOR_POR_SLOT = {
  [SLOT_ANFITRIAO]: 'janela-local',
  [SLOT_ADVERSARIO]: 'janela-rival',
};

// Jogador vazio: mantém o ecrã desenhável enquanto o documento da batalha
// ainda não chegou, sem espalhar `?.` por todo o lado.
function jogadorVazio(slot) {
  return {
    slot,
    uidAgora: slot,
    chave: slot === SLOT_ANFITRIAO ? 'A' : 'B',
    lado: slot === SLOT_ANFITRIAO ? 'esquerda' : 'direita',
    contentor: CONTENTOR_POR_SLOT[slot],
    uid: null,
    nome: slot === SLOT_ANFITRIAO ? 'Jogador A' : 'Jogador B',
    foto: '',
    pontos: 0,
    golos: 0,
    pronto: false,
    presente: false,
  };
}

function construirJogador(batalha, slot) {
  const chave = slot === SLOT_ANFITRIAO ? 'A' : 'B';
  const uid = batalha[`id${chave}`] || null;
  return {
    ...jogadorVazio(slot),
    uid,
    nome: batalha[`nome${chave}`] || (slot === SLOT_ANFITRIAO ? 'Jogador A' : 'Jogador B'),
    foto: batalha[`foto${chave}`] || '',
    pontos: Number(batalha[`pontos${chave}`] || 0),
    golos: Number(batalha[`golos${chave}`] || 0),
    pronto: !!batalha[`pronto${chave}`],
    presente: !!uid,
  };
}

// Constrói o mapa a partir do documento da batalha. É a única fonte de verdade
// do ecrã: se o documento mudar (revanche, novo adversário), o mapa muda com
// ele e nome, vídeo e placar mudam juntos — nunca cada um por seu lado.
export function construirMapaJogadores(batalha) {
  const anfitriao = batalha ? construirJogador(batalha, SLOT_ANFITRIAO) : jogadorVazio(SLOT_ANFITRIAO);
  const adversario = batalha ? construirJogador(batalha, SLOT_ADVERSARIO) : jogadorVazio(SLOT_ADVERSARIO);

  const porSlot = { [SLOT_ANFITRIAO]: anfitriao, [SLOT_ADVERSARIO]: adversario };
  const porUidAgora = { [anfitriao.uidAgora]: anfitriao, [adversario.uidAgora]: adversario };

  const porUid = {};
  if (anfitriao.uid) porUid[anfitriao.uid] = anfitriao;
  if (adversario.uid) porUid[adversario.uid] = adversario;

  return {
    porSlot,
    porUidAgora,
    porUid,
    anfitriao,
    adversario,
    jogadores: [anfitriao, adversario],
  };
}

// Qual o jogador que ocupa uma janela de vídeo (pelo uid do Agora).
export function jogadorPorUidAgora(mapa, uidAgora) {
  return mapa?.porUidAgora?.[Number(uidAgora)] || null;
}

// Em que lugar está este utilizador do Firebase; null se for espetador.
export function jogadorPorUid(mapa, uid) {
  if (!uid) return null;
  return mapa?.porUid?.[uid] || null;
}

// Que uid do Agora este utilizador deve usar ao entrar no canal.
// Determinístico e derivado do documento da batalha — nunca da ordem de
// chegada: quem for o idA é sempre o 1, toda a gente o resto é o 2.
export function uidAgoraDoUtilizador(batalha, uid) {
  if (!batalha || !uid) return null;
  return uid === batalha.idA ? SLOT_ANFITRIAO : SLOT_ADVERSARIO;
}

// Onde é que este utilizador deve mostrar a SUA própria câmara.
export function contentorLocalDoUtilizador(batalha, uid) {
  const slot = uidAgoraDoUtilizador(batalha, uid);
  return slot ? CONTENTOR_POR_SLOT[slot] : null;
}

// Onde é que a câmara de um publicador remoto deve ser mostrada.
// Serve para jogadores E espetadores: a janela vem sempre do uid, nunca de
// "quem chegou primeiro". Era este o buraco antigo — um jogador atirava
// QUALQUER publicador remoto para a janela-rival, sem sequer olhar ao uid.
export function contentorDeRemoto(uidAgora) {
  return CONTENTOR_POR_SLOT[Number(uidAgora)] || null;
}

export function euSouEspetador(batalha, uid) {
  if (!batalha || !uid) return true;
  return uid !== batalha.idA && uid !== batalha.idB;
}

// Esvazia a janela de um jogador que deixou de publicar. Sem isto ficava lá
// congelado o último fotograma — muito visível numa revanche com adversário
// novo, em que o rosto do rival anterior continuava na janela da direita.
export function limparContentorDeRemoto(uidAgora) {
  const id = contentorDeRemoto(uidAgora);
  if (!id || typeof document === 'undefined') return false;
  const elemento = document.getElementById(id);
  if (!elemento) return false;
      // // Só o ecrã da câmara: a barra de BOLAS e o resto ficam.
  return limparJanelaDeVideo(elemento);
}
